import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Check, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiClient } from '../../lib/api';

const KNOWN_NAMES = {
  SNDK: 'SanDisk Corporation',   MU:   'Micron Technology',
  INTC: 'Intel Corporation',     NVDA: 'Nvidia Corporation',
  META: 'Meta Platforms',        LRCX: 'Lam Research',
  ALAB: 'Astera Labs',           STX:  'Seagate Technology',
  AAPL: 'Apple Inc.',            MSFT: 'Microsoft',
  GOOGL: 'Alphabet Inc.',        AMZN: 'Amazon',
  TSLA: 'Tesla',                 AMD:  'Advanced Micro Devices',
  TSMC: 'Taiwan Semiconductor',  ASML: 'ASML Holding',
  WDC:  'Western Digital',       AMAT: 'Applied Materials',
  QCOM: 'Qualcomm',              TXN:  'Texas Instruments',
  AVGO: 'Broadcom',              MRVL: 'Marvell Technology',
  SMCI: 'Super Micro Computer',  ARM:  'Arm Holdings',
};

function fmtChg(n) {
  if (n == null) return null;
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function SortableRow({ stock, onToggle }) {
  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: stock.sym });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    boxShadow: isDragging ? '0 4px 16px rgba(9,9,11,.12)' : undefined,
    position: 'relative',
  };

  const chgStr = fmtChg(stock.change_pct);
  const up     = (stock.change_pct ?? 0) >= 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`add-stock-row is-selected${isDragging ? ' is-dragging' : ''}`}
      onClick={onToggle}
      role="checkbox"
      aria-checked
    >
      <span
        className="add-stock-drag"
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        aria-label="Drag to reorder"
      >
        <GripVertical size={14} />
      </span>
      <span className="add-stock-cb checked">
        <Check size={10} strokeWidth={3} />
      </span>
      <div className="add-stock-row-info">
        <span className="add-stock-sym">{stock.sym}</span>
        <span className="add-stock-name">{stock.name}</span>
      </div>
      {chgStr && (
        <span className={`add-stock-chg${up ? ' up' : ' dn'}`}>{chgStr}</span>
      )}
    </div>
  );
}

export default function AddStockModal({ currentSyms, onAdd, onRemove, onReorder, onClose }) {
  const [query, setQuery]           = useState('');
  const [allStocks, setAllStocks]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [orderedSyms, setOrderedSyms] = useState([...currentSyms]);
  const inputRef = useRef(null);

  const selectedSet = new Set(orderedSyms);
  const close = useCallback(() => onClose?.(), [onClose]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    apiClient.get('/api/market/ticker')
      .then(res => {
        const tickers = res.data?.tickers ?? [];
        const quotes  = res.data?.quotes  ?? {};
        const stocks = tickers.map(sym => ({
          sym,
          name:       KNOWN_NAMES[sym] ?? sym,
          change_pct: quotes[sym]?.change_pct ?? null,
        }));
        stocks.sort((a, b) => (b.change_pct ?? -Infinity) - (a.change_pct ?? -Infinity));
        setAllStocks(stocks);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  function toggle(stock) {
    if (selectedSet.has(stock.sym)) {
      setOrderedSyms(prev => prev.filter(s => s !== stock.sym));
      onRemove(stock.sym);
    } else {
      setOrderedSyms(prev => [...prev, stock.sym]);
      onAdd(stock.sym, stock.name);
    }
  }

  function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;
    setOrderedSyms(prev => {
      const next = arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id));
      onReorder(next);
      return next;
    });
  }

  const q = query.trim().toUpperCase();
  const stockMap = Object.fromEntries(allStocks.map(s => [s.sym, s]));

  if (q) {
    // Search mode: flat list, no drag
    const filtered = allStocks.filter(
      s => s.sym.includes(q) || s.name.toUpperCase().includes(q)
    );
    return (
      <div className="add-stock-overlay" onClick={e => { if (e.target === e.currentTarget) close(); }}>
        <div className="add-stock-modal" role="dialog" aria-modal="true" aria-label="Edit watchlist">
          {renderHeader()}
          {renderSearch()}
          <div className="add-stock-list">
            {filtered.length === 0
              ? <div className="add-stock-empty">No results for "{query}"</div>
              : filtered.map(s => renderPlainRow(s))
            }
          </div>
        </div>
      </div>
    );
  }

  // Default mode: sorted watchlist (draggable) + available
  const selectedStocks   = orderedSyms.map(sym => stockMap[sym] ?? { sym, name: KNOWN_NAMES[sym] ?? sym, change_pct: null });
  const unselectedStocks = allStocks.filter(s => !selectedSet.has(s.sym));

  function renderHeader() {
    return (
      <div className="add-stock-hdr">
        <span className="add-stock-title">Edit Watchlist</span>
        <button className="add-stock-close" onClick={close} aria-label="Close">
          <X size={15} />
        </button>
      </div>
    );
  }

  function renderSearch() {
    return (
      <div className="add-stock-search-wrap">
        <Search size={14} className="add-stock-search-icon" />
        <input
          ref={inputRef}
          className="add-stock-search-input"
          placeholder="Search by symbol or name…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query && (
          <button className="add-stock-search-clear" onClick={() => setQuery('')}>
            <X size={12} />
          </button>
        )}
      </div>
    );
  }

  function renderPlainRow(s) {
    const isSelected = selectedSet.has(s.sym);
    const chgStr     = fmtChg(s.change_pct);
    const up         = (s.change_pct ?? 0) >= 0;
    return (
      <div
        key={s.sym}
        className={`add-stock-row${isSelected ? ' is-selected' : ''}`}
        onClick={() => toggle(s)}
        role="checkbox"
        aria-checked={isSelected}
      >
        <span className="add-stock-drag" style={{ visibility: 'hidden' }}>
          <GripVertical size={14} />
        </span>
        <span className={`add-stock-cb${isSelected ? ' checked' : ''}`}>
          {isSelected && <Check size={10} strokeWidth={3} />}
        </span>
        <div className="add-stock-row-info">
          <span className="add-stock-sym">{s.sym}</span>
          <span className="add-stock-name">{s.name}</span>
        </div>
        {chgStr && (
          <span className={`add-stock-chg${up ? ' up' : ' dn'}`}>{chgStr}</span>
        )}
      </div>
    );
  }

  return (
    <div className="add-stock-overlay" onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="add-stock-modal" role="dialog" aria-modal="true" aria-label="Edit watchlist">
        {renderHeader()}
        {renderSearch()}
        <div className="add-stock-list">
          {loading && <div className="add-stock-empty">Loading stocks…</div>}
          {!loading && (
            <>
              {selectedStocks.length > 0 && (
                <>
                  {selectedStocks.length > 1 && (
                    <div className="add-stock-section-label">Your Watchlist — drag to reorder</div>
                  )}
                  {selectedStocks.length === 1 && (
                    <div className="add-stock-section-label">Your Watchlist</div>
                  )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={orderedSyms} strategy={verticalListSortingStrategy}>
                      {selectedStocks.map(s => (
                        <SortableRow key={s.sym} stock={s} onToggle={() => toggle(s)} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </>
              )}
              {unselectedStocks.length > 0 && (
                <>
                  <div className="add-stock-section-label">Available</div>
                  {unselectedStocks.map(s => renderPlainRow(s))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
